/**
 * Document → prompt-context composition, shared by every composer.
 *
 * Extracted from `chat/ChatInput.tsx` (RFC-1218 PR1) so the landing composer
 * can build the same `<attached-document>` block without importing a ~2 700-line
 * chat component. That matters beyond tidiness: the `components` Vitest project
 * runs with `isolate: false` (RFC-0849 Step 8), so a new import edge from the
 * landing into ChatInput would pull its whole provider graph into a shared
 * worker and leak background fetches into unrelated files — the exact failure
 * that `messageContent.ts` was extracted to avoid.
 */

/**
 * A document whose text was extracted for prompt context.
 *
 * Holds TEXT, never a path: the backend parses the upload in-request and stores
 * nothing, so there is no asset to reference and nothing to clean up when the
 * user removes the chip.
 */
export interface DocumentAttachment {
  type: 'document'
  filename: string
  kind: 'PDF' | 'DOCX' | 'PPTX' | 'TXT' | 'MD'
  text: string
  charCount: number
  segmentCount: number
  /**
   * RFC-1235 PR2 — embedded images that passed CSAM screening. Always the FULL
   * screenable count, never partial: a single failed image rejects the upload,
   * so an attachment existing at all means everything in it cleared.
   */
  clearedImageCount: number
}

/**
 * Defuse a literal delimiter inside extracted text.
 *
 * A document that itself contains `</attached-document>` would otherwise close
 * the block early: the model would read the remainder as instructions rather
 * than material, and the transcript stripper would leave the tail visible. The
 * zero-width space keeps the text readable while making the tag non-literal.
 */
export function neutralizeDocumentDelimiters(text: string): string {
  return text.replace(/<(\/?)attached-document/gi, '<\u200B$1attached-document')
}

function sanitizeAttributeValue(value: string): string {
  // Strip every character that could terminate or extend the attribute:
  // quotes and angle brackets close it, and a trailing backslash could escape
  // the closing quote in a consumer that unescapes the block.
  return value.replace(/[<>"'\\&]/g, '')
}

/**
 * Fold attached document text into the prompt the Director receives.
 *
 * The document is delimited and labelled rather than concatenated raw, so the
 * model can tell the user's instruction from the attached material and cite it
 * by name. The content is the user's own upload arriving on the user turn —
 * the same trust boundary as pasting it — so this adds no new injection surface
 * beyond what typing the text would.
 */
export function composePromptWithDocuments(
  message: string,
  documents: readonly DocumentAttachment[],
): string {
  if (documents.length === 0) return message
  const blocks = documents.map(
    (doc) =>
      `<attached-document name="${sanitizeAttributeValue(doc.filename)}" type="${doc.kind}" characters="${doc.charCount}">\n` +
      `${neutralizeDocumentDelimiters(doc.text)}\n` +
      `</attached-document>`,
  )
  return [message.trim(), ...blocks].filter(Boolean).join('\n\n')
}


/** One extraction still in flight, keyed so duplicate filenames stay distinct. */
export interface PendingDocument {
  id: number
  name: string
}

/**
 * Drop one settled extraction, leaving the rest pending.
 *
 * A composer cannot track "is a document extracting?" as a single string: with
 * two documents attached in quick succession, the first to settle would clear
 * the flag while the second was still in flight, and a send/Create gated on
 * that flag would fire without the second document. Keying by id (not name)
 * also stops two files that share a filename from cancelling each other.
 */
export function settlePendingDocument(
  pending: readonly PendingDocument[],
  id: number,
): readonly PendingDocument[] {
  return pending.filter((entry) => entry.id !== id)
}

/** Filename to show while extracting, or null when nothing is in flight. */
export function pendingDocumentLabel(pending: readonly PendingDocument[]): string | null {
  return pending.length > 0 ? pending[pending.length - 1].name : null
}


/**
 * Whether a composer turn may be submitted.
 *
 * A document alone is a legitimate turn — attaching a script and pressing
 * Create with no typed instruction is arguably the *primary* use of this
 * feature — so an empty prompt is not disqualifying when a document is
 * attached. An extraction still in flight IS disqualifying: submitting then
 * would drop that document from the prompt silently.
 *
 * Shared by the button's `disabled` and the submit handler so the two cannot
 * disagree, which is how a disabled-looking-but-submittable state appears.
 */
export function canSubmitComposerTurn(options: {
  trimmedPrompt: string
  documentCount: number
  extractionPending: boolean
}): boolean {
  const { trimmedPrompt, documentCount, extractionPending } = options
  if (extractionPending) return false
  return trimmedPrompt.length > 0 || documentCount > 0
}
